import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail, Play, Power, Clock, CheckCircle2, AlertCircle,
  RefreshCw, Calendar, TrendingUp, Users, Loader2, Send, Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

export default function UltraScheduler() {
  const [config, setConfig] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningExpiry, setRunningExpiry] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);

  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryHour, setExpiryHour] = useState(8);
  const [expiryMinute, setExpiryMinute] = useState(0);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const [rRes, eRes] = await Promise.all([
        axios.get(`${API}/api/ultra/email-scheduler`, { headers: authHeader() }),
        axios.get(`${API}/api/ultra/email-scheduler/expiry`, { headers: authHeader() }),
      ]);
      setConfig(rRes.data);
      setEnabled(rRes.data.enabled ?? false);
      setHour(rRes.data.hour ?? 9);
      setMinute(rRes.data.minute ?? 0);
      setExpiry(eRes.data);
      setExpiryEnabled(eRes.data.enabled ?? false);
      setExpiryHour(eRes.data.hour ?? 8);
      setExpiryMinute(eRes.data.minute ?? 0);
    } catch (e) {
      setErrorMsg('Failed to load scheduler config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const notify = (msg, isErr = false) => {
    if (isErr) setErrorMsg(msg); else setSuccessMsg(msg);
    setTimeout(() => { setSuccessMsg(''); setErrorMsg(''); }, 6000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/ultra/email-scheduler`,
        { enabled, hour: parseInt(hour), minute: parseInt(minute) }, { headers: authHeader() });
      notify(`Re-engagement ${data.enabled ? 'enabled' : 'disabled'} — runs at ${String(data.hour).padStart(2,'0')}:${String(data.minute).padStart(2,'0')} UTC`);
      await fetchConfig();
    } catch (e) { notify(e.response?.data?.detail || 'Save failed.', true); }
    finally { setSaving(false); }
  };

  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    try {
      const { data } = await axios.post(`${API}/api/ultra/email-scheduler/expiry`,
        { enabled: expiryEnabled, hour: parseInt(expiryHour), minute: parseInt(expiryMinute) }, { headers: authHeader() });
      notify(`Expiry campaign ${data.enabled ? 'enabled' : 'disabled'}`);
      await fetchConfig();
    } catch (e) { notify(e.response?.data?.detail || 'Save failed.', true); }
    finally { setSavingExpiry(false); }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/ultra/email-scheduler/run-now`, {}, { headers: authHeader() });
      notify(`Re-engagement campaign: ${data.last_sent} emails sent, ${data.last_errors} errors`);
      await fetchConfig();
    } catch (e) { notify(e.response?.data?.detail || 'Run failed.', true); }
    finally { setRunning(false); }
  };

  const handleRunExpiryNow = async () => {
    setRunningExpiry(true);
    try {
      const { data } = await axios.post(`${API}/api/ultra/email-scheduler/expiry/run-now`, {}, { headers: authHeader() });
      notify(`Expiry campaign: ${data.last_sent} emails sent, ${data.last_errors} errors`);
      await fetchConfig();
    } catch (e) { notify(e.response?.data?.detail || 'Run failed.', true); }
    finally { setRunningExpiry(false); }
  };

  const handleTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/api/ultra/email-scheduler/test-email`, {}, { headers: authHeader() });
      notify(data.sent ? `Test email sent to ${data.to}` : `Not sent: ${data.message}`);
    } catch (e) { notify(e.response?.data?.detail || 'Test failed.', true); }
    finally { setSendingTest(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const TimeSelector = ({ lbl, en, val, setV, minVal, setM }) => (
    <div className="flex items-center gap-3">
      <p className="text-sm font-semibold text-gray-800 flex items-center gap-2 w-48">
        <Clock className="h-4 w-4 text-gray-400" /> {lbl}
      </p>
      <div className="flex items-center gap-2">
        <select value={val} onChange={e => setV(e.target.value)} disabled={!en}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
          {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2,'0')}</option>)}
        </select>
        <span className="text-gray-400 font-bold">:</span>
        <select value={minVal} onChange={e => setM(e.target.value)} disabled={!en}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
          {[0,15,30,45].map(m => <option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}
        </select>
        <span className="text-xs text-gray-400">UTC</span>
      </div>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaign Scheduler</h1>
          <p className="text-sm text-gray-500 mt-1">Manage automated email campaigns for re-engagement and subscription reminders.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleTestEmail} disabled={sendingTest}
            data-testid="test-email-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50">
            {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send Test Email
          </button>
          <button onClick={fetchConfig} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition-colors">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Alerts */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />{successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />{errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-5">

          {/* ── Campaign 1: Re-engagement ─────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Re-engagement Campaign</p>
                <p className="text-xs text-gray-500">Targets users inactive for 7+ days</p>
              </div>
              <span className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config?.job_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${config?.job_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                {config?.job_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Enable Automatic Emails</p>
                <p className="text-xs text-gray-500 mt-0.5">Contractors + Exhibitors inactive 7+ days</p>
              </div>
              <button data-testid="scheduler-toggle" onClick={() => setEnabled(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          {/* Segment targeting */}
            <div>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-gray-400" /> Target Audience
              </p>
              <div className="flex gap-4">
                {[
                  { key: 'CONTRACTOR_ADMIN', label: 'Contractors', color: 'green' },
                  { key: 'EXHIBITOR_ADMIN',  label: 'Exhibitors',  color: 'blue' },
                ].map(({ key, label, color }) => {
                  const checked = (config?.target_roles || ['CONTRACTOR_ADMIN','EXHIBITOR_ADMIN']).includes(key);
                  return (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" className={`w-4 h-4 accent-${color}-600`}
                        checked={checked} disabled={!enabled}
                        onChange={() => {
                          const cur = config?.target_roles || ['CONTRACTOR_ADMIN','EXHIBITOR_ADMIN'];
                          setConfig(prev => ({...prev, target_roles: checked ? cur.filter(r => r!==key) : [...cur, key]}));
                        }} />
                      <span className={`text-sm font-medium text-${color}-700`}>{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <TimeSelector lbl="Daily Send Time" en={enabled} val={hour} setV={setHour} minVal={minute} setM={setMinute} />
            {config?.next_run && <p className="text-xs text-gray-500">Next run: {formatTime(config.next_run)}</p>}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} data-testid="scheduler-save-btn"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleRunNow} disabled={running} data-testid="run-now-btn"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                {running ? 'Running...' : 'Run Now'}
              </button>
            </div>
          </div>

          {/* ── Campaign 2: Subscription Expiry ────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Subscription Expiry Campaign</p>
                <p className="text-xs text-gray-500">Warns users 7, 3, and 1 day before subscription expires</p>
              </div>
              <span className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${expiry?.job_active ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${expiry?.job_active ? 'bg-amber-500' : 'bg-gray-400'}`} />
                {expiry?.job_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Enable Expiry Reminders</p>
                <p className="text-xs text-gray-500 mt-0.5">Helps prevent subscription churn</p>
              </div>
              <button data-testid="expiry-toggle" onClick={() => setExpiryEnabled(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${expiryEnabled ? 'bg-amber-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${expiryEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <TimeSelector lbl="Daily Check Time" en={expiryEnabled} val={expiryHour} setV={setExpiryHour} minVal={expiryMinute} setM={setExpiryMinute} />
            {expiry?.next_run && <p className="text-xs text-gray-500">Next run: {formatTime(expiry.next_run)}</p>}
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 text-xs text-amber-800">
              <strong>How it works:</strong> Checks subscriptions expiring in 7, 3, and 1 day each morning and sends personalised renewal reminder emails.
            </div>
            <div className="flex gap-3">
              <button onClick={handleSaveExpiry} disabled={savingExpiry} data-testid="expiry-save-btn"
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors">
                {savingExpiry ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                {savingExpiry ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleRunExpiryNow} disabled={runningExpiry} data-testid="run-expiry-now-btn"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors">
                {runningExpiry ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                {runningExpiry ? 'Running...' : 'Run Now'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Panel */}
        <div className="space-y-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Re-engagement Stats</p>
          {[
            { label: 'Last Run',    value: formatTime(config?.last_run), icon: Calendar, color: 'text-blue-600',  bg: 'bg-blue-50' },
            { label: 'Emails Sent', value: config?.last_sent ?? 0,       icon: Mail,     color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Errors',      value: config?.last_errors ?? 0,     icon: AlertCircle, color: config?.last_errors ? 'text-red-600' : 'text-gray-400', bg: config?.last_errors ? 'bg-red-50' : 'bg-gray-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-5 w-5 ${color}`} />
              </div>
              <div><p className="text-xs text-gray-500">{label}</p><p className="text-sm font-bold text-gray-900">{value}</p></div>
            </div>
          ))}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide pt-2">Expiry Campaign Stats</p>
          {[
            { label: 'Last Run',    value: formatTime(expiry?.last_run), icon: Calendar, color: 'text-amber-600',  bg: 'bg-amber-50' },
            { label: 'Emails Sent', value: expiry?.last_sent ?? 0,       icon: Shield,   color: 'text-green-600', bg: 'bg-green-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-5 w-5 ${color}`} />
              </div>
              <div><p className="text-xs text-gray-500">{label}</p><p className="text-sm font-bold text-gray-900">{value}</p></div>
            </div>
          ))}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <p className="text-xs font-bold text-blue-700">Campaign Tips</p>
            </div>
            <ul className="space-y-1.5">
              {['09:00–10:00 UTC yields highest open rates','Expiry reminders reduce churn by ~30%','Users inactive 7–14 days respond best'].map(tip => (
                <li key={tip} className="text-xs text-blue-700 flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>{tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
